import React, { useCallback, useEffect, useMemo, useState } from "react";
import { keyframes, styled } from "styled-components";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import { FullPageContainer, Right } from "../Dashboard/Dashboard";
import PageHeader from "../../components/PageHeader/PageHeader";
import { Card, Container, Page } from "./styled";
import { tokens } from "./tokens";
import FleetView from "./FleetView";
import SchemaEditor from "./SchemaEditor";
import TractorDetailView from "./TractorDetailView";
import { fetchConfigSchema, fetchVehicle, fetchVehicles } from "./configApi";
import { ConfigSchema, VehicleRow } from "./configTypes";
import {
  isEditable,
  requiredOnCreate,
  resolveVehicleName,
} from "./configModel";
import AddTractorDialog from "./AddTractorDialog";
import {
  Filter,
  isTestRig,
  loadSnapshotFleet,
  loadSnapshotSchema,
  NameMode,
  primaryName,
  Vehicle,
  vehicleFromRow,
} from "./data";

/** Where "show test rigs" is remembered between visits — per browser, like column widths. */
const RIGS_KEY = "tractorState.showTestRigs.v1";

type Source = "loading" | "live" | "snapshot" | "error";

/**
 * Re-apply the fleet list without throwing away what only the detail call knows.
 *
 * `GET /v1/vehicles` is authoritative for membership, identity, config and the
 * counts — but it deliberately carries no `open_tickets` and no
 * `config_history`; those exist only on `GET /v1/vehicles/{name}`. Replacing the
 * array wholesale therefore blanked the ticket list and the history of the
 * tractor being *looked at*, and the two loads race: a token renew re-runs both,
 * and the fleet-wide call (which joins Jira for every vehicle) lands after the
 * single-vehicle one. The result was a detail page that sat there for an hour
 * and then quietly changed to "No open tickets".
 *
 * So the list updates everything it actually knows and leaves the rest alone.
 * `applyRow` has always done this for the write path; this is the same rule for
 * the read path, which never had it.
 */
function keepDetail(prev: Vehicle[], next: Vehicle[]): Vehicle[] {
  if (prev.length === 0) return next;
  const loaded = new Map(prev.map((v) => [v.vehicleName, v]));
  return next.map((v) => {
    const had = loaded.get(v.vehicleName);
    if (!had) return v;
    return {
      ...v,
      tickets: v.tickets.length ? v.tickets : had.tickets,
      history: v.history.length ? v.history : had.history,
    };
  });
}

/**
 * Tractor State — AeroVect's fleet readiness and tractor-config console.
 *
 * One `GET /v1/vehicles` supplies the roster, the config record, the Jira
 * summary (the fleet rollup it also carries is not shown); one `GET
 * /v1/config-schema` supplies every
 * label, vocabulary and edit permission the views render from.
 *
 * The page has exactly one fleet at a time and says which one it is. A token
 * means the live API and nothing else: no snapshot underneath it while it loads,
 * none substituted if it fails — a stale fleet standing in silently for a live
 * one is worse than no fleet, because the reader cannot tell the difference. No
 * token means isolated dev, where the committed snapshot is all there is and the
 * page is read-only: it carries no `config_version`, so a write from it could
 * not be guarded against a concurrent one.
 *
 * The fleet grid and the per-tractor detail are real routes
 * (/tractor-state and /tractor-state/:vehicleName) so the browser back button
 * moves between them. Filter, sort, presentation and the name toggle live here
 * so they persist across that navigation.
 */
interface TractorStateProps {
  /**
   * Cognito ID token for the fleet_state API, supplied by TractorStateLive once
   * the user is signed in. Absent only on the isolated-dev path (snapshot,
   * read-only), which is why the live load and every write are gated on it.
   */
  token?: string;
}

/** The tractor a `/tractor-state/:vehicleName` path names, or null on the grid. */
const vehicleFromPath = (pathname: string): string | null => {
  const rest = pathname.replace(/^\/tractor-state\/?/, "").split("/")[0];
  if (!rest) return null;
  try {
    return decodeURIComponent(rest);
  } catch {
    return rest;
  }
};

const TractorState = ({ token }: TractorStateProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [filter, setFilter] = useState<Filter>("all");
  const [nameMode, setNameMode] = useState<NameMode>("display");
  const [adding, setAdding] = useState(false);

  // No rows until a load settles. Seeding them from the snapshot instead meant
  // every load of the page rendered the pre-live console — stale ticket counts,
  // a roster fixed at snapshot time, no capability matrix,
  // every state badge blank — and then replaced it under the reader.
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [schema, setSchema] = useState<ConfigSchema | null>(null);
  // Rigs and simulators are hidden from the fleet by default; the choice is
  // per browser, like the fleet table's column widths.
  const [showRigs, setShowRigs] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(RIGS_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(RIGS_KEY, showRigs ? "1" : "0");
    } catch {
      // Private mode: the choice just doesn't survive the reload.
    }
  }, [showRigs]);
  const [source, setSource] = useState<Source>("loading");
  /** What the live API said when it refused, shown verbatim in the error panel. */
  const [loadError, setLoadError] = useState<string | null>(null);
  /** When the snapshot was built. Only set on the isolated-dev path. */
  const [snapshotAt, setSnapshotAt] = useState<string | null>(null);
  /** Bumped by "Try again", which is the whole reason the load is an effect. */
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    if (!token) {
      // Isolated dev: no Cognito session, so no API to call. The committed
      // snapshot is the only fleet here, and it exists in a dev build alone —
      // a deployed build resolves null and says so rather than showing zero
      // tractors as though the fleet were empty.
      // Fleet and schema together, exactly as the live path loads them: the
      // views render vocabulary out of the schema, so a fleet without one shows
      // raw values and an empty config panel.
      Promise.all([loadSnapshotFleet(), loadSnapshotSchema()]).then(
        ([fleet, loadedSchema]) => {
          if (cancelled) return;
          if (!fleet) {
            setLoadError(
              "This build has no live API configured and no snapshot.",
            );
            setSource("error");
            return;
          }
          setVehicles(fleet.vehicles);
          setSchema(loadedSchema);
          setSnapshotAt(fleet.generatedAt);
          setSource("snapshot");
        },
      );
      return () => {
        cancelled = true;
      };
    }

    // A silent token renew re-runs this an hour in, with live rows already on
    // screen. Keep rendering them: reverting a working page to its loading
    // state to re-fetch what it already has is the same flicker in miniature.
    setSource((prev) => (prev === "live" ? "live" : "loading"));
    // Both or neither: the views render vocabulary out of the schema, so a
    // fleet without its schema would show raw enum values and no edit
    // affordances. Failing together keeps the page coherent.
    Promise.all([fetchVehicles(token), fetchConfigSchema(token)])
      .then(([fleet, loadedSchema]) => {
        if (cancelled) return;
        setVehicles((prev) =>
          keepDetail(prev, fleet.vehicles.map(vehicleFromRow)),
        );
        // Absent for a vendor tenant; the strip renders nothing rather than
        // zeroes, which would read as a fleet of no tractors.
        setSchema(loadedSchema);
        setLoadError(null);
        setSource("live");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : null);
        setSource("error");
      });
    return () => {
      cancelled = true;
    };
  }, [token, reloadKey]);

  const retry = useCallback(() => setReloadKey((k) => k + 1), []);

  /**
   * Fold a server row back into page state.
   *
   * A PATCH response and the single-vehicle GET are both authoritative for the
   * row, but only the GET carries `open_tickets`. Preserving the tickets we
   * already hold when they're absent stops a successful save from blanking the
   * ticket list beside it.
   */
  const applyRow = useCallback((row: VehicleRow) => {
    setVehicles((prev) =>
      prev.map((existing) => {
        if (existing.vehicleName !== row.vehicle_name) return existing;
        const next = vehicleFromRow(row);
        return row.open_tickets === undefined
          ? { ...next, tickets: existing.tickets }
          : next;
      }),
    );
  }, []);

  const openTractor = (vehicleName: string) =>
    navigate(`/tractor-state/${encodeURIComponent(vehicleName)}`);
  const goFleet = () => navigate("/tractor-state");

  const readOnlyReason = useMemo(() => {
    if (source === "snapshot") {
      return "This is an offline copy of the fleet, so config can’t be edited.";
    }
    if (!token) return "Sign in to edit tractor config.";
    if (source !== "live") return "Waiting for live data.";
    if (schema && !schema.fields.some(isEditable)) {
      return `Your role (${schema.roles.join(", ")}) can view this record but not change it.`;
    }
    return null;
  }, [token, source, schema]);

  /**
   * Where the reader is, for the page header and the browser tab.
   *
   * The header used to read "Tractor State" on every route while an H1 below
   * it read "Fleet" and then the tractor's name — two title-sized lines saying
   * one thing, and a tab that always said "AeroVect Fleet". The header now
   * carries the crumb the way the Vehicles pages do, and the tab names the
   * tractor, so a shared link previews as the tractor it points at.
   */
  const openName = vehicleFromPath(pathname);
  const openVehicle = useMemo(
    () =>
      openName
        ? (vehicles.find((x) => x.vehicleName === openName) ?? null)
        : null,
    [vehicles, openName],
  );
  // The open tractor's name, for the browser tab. The page header itself says
  // "Tractor State" at both levels — see the PageHeader note below.
  const openLabel = openVehicle ? primaryName(openVehicle, nameMode) : openName;

  useEffect(() => {
    const previous = document.title;
    document.title = openLabel
      ? `${openLabel} · Tractor State`
      : "Tractor State · AeroVect Fleet";
    return () => {
      document.title = previous;
    };
  }, [openLabel]);

  /**
   * Whether this caller may onboard a tractor — derived, not hardcoded. The API
   * makes create fleet-admin-only by requiring fields that are fleet-admin-only,
   * so "can I write every required-on-create field" is the same question, asked
   * of the schema instead of of a role name.
   */
  const canCreate = useMemo(() => {
    if (!token || source !== "live" || !schema) return false;
    const required = requiredOnCreate(schema.fields);
    return required.length > 0 && required.every((f) => f.can_edit);
  }, [token, source, schema]);

  /**
   * Whether this caller may edit the schema: live, and holding the fleet-admin
   * group. The API enforces the same (and the internal tenant); this only
   * decides whether to show the door.
   */
  const canEditSchema = useMemo(
    () =>
      !!token &&
      source === "live" &&
      !!schema &&
      schema.roles.some((r) => r === "fleet-admin" || r === "fleet:admin"),
    [token, source, schema],
  );

  /** A newly onboarded tractor joins the fleet and opens straight away. */
  const onCreated = useCallback(
    (row: VehicleRow) => {
      setVehicles((prev) => [vehicleFromRow(row), ...prev]);
      setAdding(false);
      navigate(`/tractor-state/${encodeURIComponent(row.vehicle_name)}`);
    },
    [navigate],
  );

  const snapshotDay = (snapshotAt ?? "").slice(0, 10);
  const subtitle =
    source === "snapshot"
      ? `ATL · offline copy of the fleet from ${snapshotDay} · read-only`
      : `ATL · live · ${
          showRigs
            ? vehicles.length
            : vehicles.filter((v) => !isTestRig(v)).length
        } tractors · open Jira tickets per tractor`;

  return (
    <Shell>
      <Navbar />
      <ShellRight>
        {/*
          The shared page header, saying the same thing at both levels. It used
          to turn into a breadcrumb on the tractor page — "Tractor State" shrunk
          to a 20px grey link beside the tractor's name at 40px bold — which read
          as two unrelated pieces of type, and repeated a name the page already
          sets 60px below it, large. The header is app chrome: it names the
          section, like every sibling page's does. The tractor page names the
          tractor itself, in its title row, with "‹ All tractors" as the way
          back; the browser tab still carries the tractor's name.
        */}
        <PageHeader>Tractor State</PageHeader>

        <Page>
          {source === "loading" && (
            <SourceBanner $tone="info" role="status">
              Loading the fleet…
            </SourceBanner>
          )}
          {source === "snapshot" && (
            <SourceBanner $tone="warn">
              Offline development data — a saved copy of the fleet from{" "}
              {snapshotDay}, not the live fleet. Config is read-only. Run{" "}
              <code>npm run start-beta</code> for live data.
            </SourceBanner>
          )}
          {source === "loading" ? (
            <LoadingSkeleton />
          ) : source === "error" ? (
            <LoadFailed detail={loadError} onRetry={retry} />
          ) : (
            <Routes>
              <Route
                index
                element={
                  <FleetView
                    vehicles={vehicles}
                    schema={schema}
                    subtitle={subtitle}
                    filter={filter}
                    onFilter={setFilter}
                    nameMode={nameMode}
                    onNameMode={setNameMode}
                    onAddTractor={canCreate ? () => setAdding(true) : undefined}
                    onEditSchema={
                      canEditSchema
                        ? () => navigate("/tractor-state/schema")
                        : undefined
                    }
                    showRigs={showRigs}
                    onShowRigs={setShowRigs}
                    onOpen={openTractor}
                  />
                }
              />
              <Route
                path="schema"
                element={
                  canEditSchema && schema && token ? (
                    <SchemaEditor
                      schema={schema}
                      token={token}
                      onSaved={setSchema}
                      onBack={goFleet}
                    />
                  ) : (
                    <Navigate to="/tractor-state" replace />
                  )
                }
              />
              <Route
                path=":vehicleName"
                element={
                  <TractorDetailRoute
                    vehicles={vehicles}
                    schema={schema}
                    token={source === "live" ? token : undefined}
                    readOnlyReason={readOnlyReason}
                    onRow={applyRow}
                    filter={filter}
                    onFilter={setFilter}
                    showRigs={showRigs}
                    nameMode={nameMode}
                    onNameMode={setNameMode}
                    onOpen={openTractor}
                    onBack={goFleet}
                  />
                }
              />
              <Route
                path="*"
                element={<Navigate to="/tractor-state" replace />}
              />
            </Routes>
          )}
          {adding && schema && token && (
            <AddTractorDialog
              schema={schema}
              token={token}
              existingNames={vehicles.map((x) => x.vehicleName)}
              onCreated={onCreated}
              onCancel={() => setAdding(false)}
            />
          )}
        </Page>
        <Footer />
      </ShellRight>
    </Shell>
  );
};

/**
 * The shell, with the vertical scroll given to `<Page>` instead of the window.
 *
 * `Page` was always written to be the scroller — `flex: 1`, `min-height: 0`,
 * `overflow-y: auto`, `scrollbar-gutter: stable`. It never got to be one: the
 * shared shell is `height: 100%; min-height: 100vh`, so it grows with its
 * content, `Page` is sized to fit, and the *window* scrolls instead. `Page`
 * still establishes a scroll container though, and a scroll container that
 * never scrolls silently disables `position: sticky` for everything inside it —
 * which is why the config panel's sticky footer had no effect either.
 *
 * Capping the shell at the viewport hands the scroll back to `Page`: the nav and
 * the page header stay put, the content scrolls under them, and a sticky child
 * pins. Scoped to this route rather than fixed in `Dashboard.tsx` because that
 * shell is shared with four other pages whose layouts were built against its
 * current behaviour.
 *
 * Only above the shell's own breakpoint, where the nav is a sidebar. Below it
 * the nav stacks on top and a viewport-capped shell would fight the mobile
 * keyboard; there the window keeps the scroll and nothing pins, which is the
 * behaviour that page has today.
 */
const Shell = styled(FullPageContainer)`
  @media (min-width: 1033px) {
    height: 100vh;
    min-height: 0;
    overflow: hidden;
  }
`;

/**
 * The content column beside the nav — and the one place the fleet table's width
 * is allowed to stop mattering.
 *
 * `Right` is a flex item of a row-direction shell, so its automatic minimum size
 * is its content's min-content width. The fleet table is ~3300px wide, so that
 * minimum was ~3300px: the column grew to fit the table, the shell clipped the
 * overflow, and the table's own scroll box — sized to the column, not the
 * window — never had anything to scroll. What that looks like is a table stuck
 * at one width with its right-hand columns unreachable, no matter how the window
 * is sized, which is the bug this whole change is about. `min-width: 0` lets the
 * column take the viewport's width instead of the content's, and the overflow
 * lands where it belongs: inside the table's box.
 *
 * Scoped here rather than fixed on `Right` itself, which four other pages are
 * laid out against.
 */
const ShellRight = styled(Right)`
  min-width: 0;

  @media (min-width: 1033px) {
    height: auto;
    min-height: 0;
  }
`;

/**
 * What a live build renders until its first load settles.
 *
 * The alternative — rendering the committed snapshot meanwhile — showed the
 * reader a fleet that was wrong in every way the live data exists to correct,
 * and it decided routing on that fleet: a deep link to a tractor onboarded since
 * the snapshot was built resolved to nothing and bounced to the grid before the
 * real roster ever arrived. Nothing here claims to be data.
 */
const LoadingSkeleton = () => (
  <SkeletonPage aria-busy="true" aria-label="Loading fleet">
    <SkeletonTitle />
    <SkeletonSubtitle />
    <SkeletonControls />
    <SkeletonTable />
  </SkeletonPage>
);

/**
 * What a failed load shows: the reason, and nothing else.
 *
 * The page used to substitute the committed snapshot here, under a banner that
 * said so. But a fleet grid that renders is a fleet grid an operator reads, and
 * one built from a months-old join of the beta tables answers "is dl13 grounded"
 * with yesterday's news. An honest dead end they can retry beats a plausible
 * wrong answer.
 */
const LoadFailed = ({
  detail,
  onRetry,
}: {
  detail: string | null;
  onRetry: () => void;
}) => (
  <Container $maxWidth={1280}>
    <FailCard role="alert">
      <FailTitle>Couldn’t load the fleet</FailTitle>
      <FailDetail>{detail ?? "The fleet API didn’t answer."}</FailDetail>
      <FailBody>
        Nothing is shown rather than a stale copy of the fleet, which would read
        as the real thing.
      </FailBody>
      <RetryButton type="button" onClick={onRetry}>
        Try again
      </RetryButton>
    </FailCard>
  </Container>
);

interface DetailRouteProps {
  vehicles: Vehicle[];
  schema: ConfigSchema | null;
  token?: string;
  readOnlyReason: string | null;
  onRow: (row: VehicleRow) => void;
  filter: Filter;
  showRigs: boolean;
  onFilter: (f: Filter) => void;
  nameMode: NameMode;
  onNameMode: (m: NameMode) => void;
  onOpen: (vehicleName: string) => void;
  onBack: () => void;
}

/**
 * Resolves :vehicleName to a vehicle and loads its full record.
 *
 * The segment is normally a roster name, but the console used to key this route
 * on the Jira asset id, so `/tractor-state/DL-13` links exist in the wild.
 * Those are resolved against the loaded fleet and redirected — never mapped
 * through a hardcoded table, since the roster name isn't derivable from the
 * Jira id.
 */
const TractorDetailRoute: React.FC<DetailRouteProps> = ({
  vehicles,
  schema,
  token,
  readOnlyReason,
  onRow,
  filter,
  showRigs,
  onFilter,
  nameMode,
  onNameMode,
  onOpen,
  onBack,
}) => {
  const { vehicleName } = useParams<{ vehicleName: string }>();
  const resolved = resolveVehicleName(vehicleName, vehicles);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Tickets and change history only exist on the single-vehicle route; the
    // grid deliberately doesn't carry them.
    if (!token || !resolved) return;
    let cancelled = false;
    setLoading(true);
    fetchVehicle(token, resolved)
      .then((row) => !cancelled && onRow(row))
      // Leave the grid's row rendered rather than erroring the whole view: it
      // already has identity, config and counts — only tickets and history are
      // missing, and the panel says so.
      .catch(() => undefined)
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [token, resolved, onRow]);

  if (!resolved) return <Navigate to="/tractor-state" replace />;
  if (resolved !== vehicleName) {
    return <Navigate to={`/tractor-state/${resolved}`} replace />;
  }

  const vehicle = vehicles.find((v) => v.vehicleName === resolved);
  if (!vehicle) return <Navigate to="/tractor-state" replace />;

  return (
    <TractorDetailView
      vehicle={vehicle}
      vehicles={vehicles}
      schema={schema}
      token={token}
      readOnlyReason={readOnlyReason}
      detailLoading={loading}
      onSaved={onRow}
      filter={filter}
      showRigs={showRigs}
      onFilter={onFilter}
      nameMode={nameMode}
      onNameMode={onNameMode}
      onOpen={onOpen}
      onBack={onBack}
    />
  );
};

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

const Shimmer = styled.div`
  /* SkeletonPage is a flex column; only the table placeholder may absorb slack. */
  flex: none;
  background: ${tokens.white};
  border: 1px solid ${tokens.line};
  border-radius: 8px;
  animation: ${pulse} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// Each placeholder stands in for the block that lands there, so the page does
// not jump when the real thing replaces it.
const SkeletonTitle = styled(Shimmer)`
  width: 148px;
  height: 34px;
  margin-bottom: 10px;
`;

const SkeletonSubtitle = styled(Shimmer)`
  width: 300px;
  height: 14px;
  margin-bottom: 22px;
`;

const SkeletonControls = styled(Shimmer)`
  height: 30px;
  width: 420px;
  max-width: 100%;
  border-radius: 999px;
  margin-bottom: 14px;
`;

// The table claims the rest of the column, exactly as the real one does, so the
// page does not resize when the fleet lands.
const SkeletonTable = styled(Shimmer)`
  flex: 1;
  min-height: 220px;
  border-radius: 14px;
`;

// Mirrors FleetView's own column so each placeholder sits where its block will.
const SkeletonPage = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  padding: 32px 40px 28px;

  @media (max-width: 760px) {
    padding: 20px 16px 24px;
  }
`;

const FailCard = styled(Card)`
  max-width: 560px;
  padding: 26px 28px;
`;

const FailTitle = styled.div`
  font-size: 19px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const FailDetail = styled.div`
  font-family: ${tokens.fontMono};
  font-size: 12.5px;
  color: ${tokens.groundedText};
  margin-bottom: 12px;
  overflow-wrap: anywhere;
`;

const FailBody = styled.div`
  font-size: 13.5px;
  color: ${tokens.slate};
  margin-bottom: 18px;
`;

const RetryButton = styled.button`
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  border-radius: 999px;
  padding: 8px 18px;
  cursor: pointer;
  background: ${tokens.white};
  border: 1px solid ${tokens.line};
  color: ${tokens.ink};

  &:hover {
    border-color: ${tokens.blue};
    color: ${tokens.blue};
  }
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 2px;
  }
`;

const SourceBanner = styled.div<{ $tone: "info" | "warn" }>`
  flex: none;
  margin: 16px 40px 0;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 13px;
  border: 1px solid
    ${(p) =>
      p.$tone === "warn" ? "rgba(255,150,0,0.4)" : "rgba(0,105,255,0.25)"};
  background: ${(p) =>
    p.$tone === "warn" ? "rgba(255,150,0,0.1)" : "rgba(0,105,255,0.06)"};
  color: #222a35;

  @media (max-width: 760px) {
    margin: 12px 16px 0;
  }
`;

export default TractorState;
